import { useState, useEffect } from 'react';

// FBR status chip for invoice rows — mounted ONLY when the business runs FBR
// (the parent checks fbrEnabled), so an FBR-off business never sees it.
// Green = accepted by FBR (shows FBR's invoice number); amber/red = needs a
// human look, with a resend button; no status = an invoice made before FBR
// was switched on, offered a quiet "send" chip.
const fbrChipStyle = (color, clickable = false) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 9px',
  borderRadius: '999px',
  border: `1px solid ${color}`,
  color,
  background: 'transparent',
  fontSize: '0.68rem',
  fontWeight: 700,
  cursor: clickable ? 'pointer' : 'default',
  whiteSpace: 'nowrap',
  lineHeight: 1.7,
});

export function FbrBadge({ invoice, onResend }) {
  const status = invoice.fbr_status || '';
  if (status === 'SUBMITTED') {
    return (
      <span
        title={`FBR Invoice No. ${invoice.fbr_invoice_number}`}
        style={fbrChipStyle('#16a34a')}
      >
        FBR ✓ {invoice.fbr_invoice_number}
      </span>
    );
  }
  if (status === 'PENDING') {
    return (
      <button
        type="button"
        onClick={() => onResend(invoice)}
        title={invoice.fbr_error || 'FBR did not answer — check the portal before resending.'}
        style={fbrChipStyle('#d97706', true)}
      >
        FBR ⏳ Tap to check
      </button>
    );
  }
  if (status === 'FAILED') {
    return (
      <button
        type="button"
        onClick={() => onResend(invoice)}
        title={invoice.fbr_error || 'FBR rejected this invoice — tap to resend after fixing.'}
        style={fbrChipStyle('#dc2626', true)}
      >
        FBR ✗ Tap to resend
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onResend(invoice)}
      title="Send this invoice to FBR"
      style={fbrChipStyle('#64748b', true)}
    >
      FBR ↗ Send
    </button>
  );
}

// The FBR strip printed at the bottom of a fiscalised invoice: FBR's own
// invoice number plus the QR code FBR's spec asks for (v2.0, 1 inch square —
// the QR carries the invoice number, which is what FBR's verifier reads).
// Mounted only when this business runs FBR AND this invoice was accepted,
// so every pre-FBR print looks exactly as it always did.
export function FbrPrintBlock({ invoice }) {
  const number = invoice?.fbr_invoice_number || '';
  const [qrDataUrl, setQrDataUrl] = useState('');

  useEffect(() => {
    let alive = true;
    if (number) {
      // qrcode (~40 kB) loads on demand: it is only needed once a fiscalised
      // invoice's print block actually renders.
      import('qrcode')
        .then(({ default: QRCode }) => QRCode.toDataURL(number, { width: 256, margin: 1, errorCorrectionLevel: 'M' }))
        .then(url => { if (alive) setQrDataUrl(url); })
        .catch(() => { if (alive) setQrDataUrl(''); });
    } else {
      setQrDataUrl('');
    }
    return () => { alive = false; };
  }, [number]);

  if (!number || invoice?.fbr_status !== 'SUBMITTED') return null;

  return (
    <div
      className="fbr-print-block"
      style={{
        marginTop: '18px',
        paddingTop: '12px',
        borderTop: '1px dashed #94a3b8',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        breakInside: 'avoid',
      }}
    >
      {qrDataUrl && (
        <img
          src={qrDataUrl}
          alt="FBR QR"
          style={{ width: '96px', height: '96px', flexShrink: 0 }} /* 1 inch square */
        />
      )}
      <div style={{ fontSize: '0.82rem', color: '#1e293b', lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>FBR Digital Invoicing</div>
        <div>
          Invoice No.: <b style={{ fontSize: '0.95rem' }}>{number}</b>
        </div>
        {invoice?.fbr_submitted_at && (() => {
          let when = String(invoice.fbr_submitted_at);
          try { when = new Date(invoice.fbr_submitted_at).toLocaleString(); } catch { /* keep raw */ }
          return <div style={{ color: '#64748b', fontSize: '0.75rem' }}>Submitted: {when}</div>;
        })()}
      </div>
    </div>
  );
}
